import { useCallback, useEffect, useRef, useState } from "react";
import userJobApi from "@/api/userJob";
import { Alert } from "@/components/Alert/Alert";
import { Modal } from "@/components/Modal/Modal";
import { ModalFooter } from "@/components/Modal/ModalFooter";
import { AppText } from "@/components/Text/AppText";
import { UserJobForm, UserJobFormRef } from "@/components/UserJob/UserJobForm";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useInvalidateUserJobsCache } from "@/hooks/useAppData";
import { getIsModalOpen, getModalProps } from "@/store/modal/selectors";
import { closeModal } from "@/store/modal/slice";
import { getUserJobById, getUserJobs } from "@/store/userJob/selectors";
import { updateUserJob } from "@/store/userJob/slice";
import { IUserJob } from "@/types";
import { ModalTypes } from "@/types/modal";

const ManageUserJobModal = () => {
    const isOpen = useAppSelector(getIsModalOpen(ModalTypes.MANAGE_USER_JOB));
    const modalProps = useAppSelector(
        getModalProps(ModalTypes.MANAGE_USER_JOB),
    );
    const [activeJob, setActiveJob] = useState<IUserJob | null>(null);
    const dispatch = useAppDispatch();
    const userJob = useAppSelector(state =>
        getUserJobById(state, modalProps?.userJobId),
    );
    const allUserJobs = useAppSelector(getUserJobs);
    const formRef = useRef<UserJobFormRef>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const invalidateUserJobs = useInvalidateUserJobsCache();

    useEffect(() => {
        if (modalProps?.userJobId) {
            const jobDetails = allUserJobs.find(
                job => job.id === modalProps.userJobId,
            );
            if (jobDetails) {
                setActiveJob(jobDetails);
            }
        }
    }, [modalProps?.userJobId, allUserJobs]);

    const onSave = useCallback(
        async (userJob: IUserJob) => {
            try {
                setIsLoading(true);
                if (userJob.id === "new") {
                    const newJob = await userJobApi.saveUserJob({
                        ...userJob,
                        id: undefined,
                    });
                    invalidateUserJobs();
                    dispatch(closeModal(ModalTypes.MANAGE_USER_JOB));
                    dispatch(updateUserJob(newJob));
                } else {
                    await userJobApi.saveUserJob(userJob, userJob.id);
                    invalidateUserJobs();
                    dispatch(closeModal(ModalTypes.MANAGE_USER_JOB));
                    dispatch(updateUserJob(userJob));
                }
            } catch (error) {
                setError("Something went wrong, please try again.");
                setIsLoading(false);
            }
        },
        [dispatch, invalidateUserJobs],
    );

    return (
        <Modal
            modalType={ModalTypes.MANAGE_USER_JOB}
            open={isOpen}
            title={userJob ? "Edit Job" : "Create a Job"}
            width="4xl"
            mobileFullScreen={true}
            afterClose={() => {
                setActiveJob(null);
                setIsLoading(false);
                setError(null);
            }}
            footer={
                <ModalFooter
                    primaryActionText="Save"
                    primaryActionOnClick={() => {
                        formRef.current?.submitForm();
                    }}
                    secondaryActionText="Cancel"
                    secondaryActionOnClick={() => {
                        dispatch(closeModal(ModalTypes.MANAGE_USER_JOB));
                    }}
                    primaryActionLoading={isLoading}
                />
            }
        >
            <AppText
                variant="regular"
                className="mb-4 text-neutral-700"
            >
                Track every opportunity and unlock the full power of{" "}
                <AppText
                    variant="regular"
                    className="!font-medium"
                    as="span"
                    applyGradient
                >
                    Smart Scribe
                </AppText>{" "}
                and{" "}
                <AppText
                    variant="regular"
                    className="!font-medium"
                    as="span"
                    applyGradient
                >
                    Smart Match
                </AppText>{" "}
                boosting your resumes for real results.
            </AppText>
            {error && (
                <div className="mb-4">
                    <Alert
                        message={error}
                        type="error"
                        dismissable
                    />
                </div>
            )}
            {activeJob && (
                <UserJobForm
                    ref={formRef}
                    userJob={activeJob || undefined}
                    onSave={onSave}
                />
            )}
            {!activeJob && (
                <UserJobForm
                    ref={formRef}
                    onSave={onSave}
                />
            )}
        </Modal>
    );
};

export default ManageUserJobModal;
